import React, { useState } from 'react';

export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleDelete = async () => {
    setStatus('loading');
    try {
      // Login first
      const loginRes = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!loginRes.ok) {
        setStatus('error');
        setMessage('Invalid email or password.');
        return;
      }

      const { access_token } = await loginRes.json();

      // Delete account
      const deleteRes = await fetch(`${API_URL}/api/user/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${access_token}` },
      });

      if (deleteRes.ok) {
        setStatus('success');
        setMessage('Your account and all associated data have been permanently deleted.');
      } else {
        setStatus('error');
        setMessage('Failed to delete account. Please try again or contact support.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.checkmark}>&#10003;</div>
          <h1 style={styles.title}>Account Deleted</h1>
          <p style={styles.text}>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Delete Your UniPic Account</h1>
        <p style={styles.text}>
          This will permanently delete your account and all associated data including photos, 
          face enrollment data, messages, and location history. This action cannot be undone.
        </p>

        {!showConfirm ? (
          <button
            style={styles.deleteButton}
            onClick={() => setShowConfirm(true)}
            data-testid="show-confirm-btn"
          >
            I want to delete my account
          </button>
        ) : (
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              data-testid="delete-email-input"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              data-testid="delete-password-input"
            />

            {status === 'error' && <p style={styles.error}>{message}</p>}

            <button
              style={styles.confirmButton}
              onClick={handleDelete}
              disabled={status === 'loading' || !email || !password}
              data-testid="confirm-delete-btn"
            >
              {status === 'loading' ? 'Deleting...' : 'Permanently Delete My Account'}
            </button>

            <button
              style={styles.cancelButton}
              onClick={() => { setShowConfirm(false); setStatus(null); setMessage(''); }}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#111827',
    padding: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: '#1f2937',
    borderRadius: 12,
    padding: '40px 32px',
    maxWidth: 440,
    width: '100%',
    textAlign: 'center',
  },
  title: {
    color: '#f9fafb',
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 0,
  },
  text: {
    color: '#9ca3af',
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 24,
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#374151',
    border: '1px solid #4b5563',
    borderRadius: 8,
    color: '#f9fafb',
    fontSize: 15,
    marginBottom: 12,
    outline: 'none',
    boxSizing: 'border-box',
  },
  deleteButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 8,
  },
  cancelButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: 'transparent',
    color: '#9ca3af',
    border: '1px solid #4b5563',
    borderRadius: 8,
    fontSize: 15,
    cursor: 'pointer',
  },
  error: {
    color: '#ef4444',
    fontSize: 13,
    marginBottom: 12,
  },
  checkmark: {
    fontSize: 48,
    color: '#10b981',
    marginBottom: 12,
  },
};
